// src/components/worldrated/WorldRated.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./WorldRated.css";
import WorldRatedDetail from "./WorldRatedDetail.js";

/* ---------- Small UI bits ---------- */
const ViewToggle = ({ viewMode = "hierarchy", onSetViewMode }) => (
  <div className="wr-viewtoggle">
    <button
      type="button"
      className={`wr-pill ${viewMode === "hierarchy" ? "wr-pill--active" : ""}`}
      aria-pressed={viewMode === "hierarchy"}
      onClick={() => onSetViewMode?.("hierarchy")}
    >
      Browse by Region
    </button>
    <button
      type="button"
      className={`wr-pill ${viewMode === "alphabetical" ? "wr-pill--active" : ""}`}
      aria-pressed={viewMode === "alphabetical"}
      onClick={() => onSetViewMode?.("alphabetical")}
    >
      A–Z by Hotel
    </button>
  </div>
);

const Pill = ({ active, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`wr-chip ${active ? "wr-chip--active" : ""}`}
    aria-pressed={!!active}
  >
    {children}
  </button>
);

/* ---------- Hotel Card (Luxe tile version) ---------- */

// choose a background from official hotel images ONLY
const HotelCard = ({ h = {}, onSelect }) => {
  // rating stars
  const rating = useMemo(() => {
    const n = parseInt(h.rating, 10);
    if (Number.isNaN(n)) return 0;
    return Math.max(0, Math.min(5, n));
  }, [h.rating]);

  // pick a random full URL from h.images (raw, no encoding)
  const bgImage = useMemo(() => {
    if (!Array.isArray(h.images) || h.images.length === 0) return "";

    // filter for truthy strings
    const clean = h.images.filter(
      (img) => typeof img === "string" && img.trim() !== ""
    );
    if (clean.length === 0) return "";

    const idx = Math.floor(Math.random() * clean.length);
    return clean[idx]; // raw string
  }, [h.images]);

  const cityCountry = [h.city, h.country].filter(Boolean).join(", ");

  // debug helper: if nothing is showing up for a hotel, check console
  if (!bgImage) {
    // eslint-disable-next-line no-console
    console.warn("No bgImage for:", h.name, h.images);
  }

  return (
    <button
      type="button"
      className={`wr-hotelcard wr-reveal ${bgImage ? "wr-hasbg" : "wr-nobg"}`}
      onClick={() => onSelect?.(h)}
      style={
        bgImage
          ? { backgroundImage: `url("${bgImage}")` }
          : undefined
      }
      aria-label={`Open details for ${h.name || "hotel"}`}
    >
      {/* dark gradient veil over the bg image */}
      <div className="wr-hotelcard__veil" />

      <div className="wr-hotelcard__inner">
        <div className="wr-hotelcard__textblock">
          <h3 className="wr-hotelcard__title">
            {h.name || "Untitled Hotel"}
          </h3>

          <div className="wr-hotelcard__meta">
            {cityCountry && (
              <div className="wr-hotelcard__loc">{cityCountry}</div>
            )}
            {h.continent && (
              <div className="wr-hotelcard__cont">{h.continent}</div>
            )}
          </div>

          <div
            className="wr-hotelcard__stars"
            aria-label={`Rating ${rating} of 5`}
          >
            {"★".repeat(rating)}
            <span className="dim">{"★".repeat(5 - rating)}</span>
          </div>

          {/* fallback note if there's no bg */}
          {!bgImage && (
            <div className="wr-hotelcard__placeholderNote">
              Image coming soon
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

/* ---------- Hero w/ background crossfade ---------- */
function Hero({ imgs = [], onScrollCueClick }) {
  const [activeIdx, setActiveIdx] = useState(0);

  // auto-cycle hero background images
  useEffect(() => {
    if (!imgs.length) return;
    const id = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % imgs.length);
    }, 6000); // ~5s hold + 1s fade
    return () => clearInterval(id);
  }, [imgs.length]);

  return (
    <section className="wr-hero">
      {/* stacked background layers */}
      <div className="wr-hero__imgstack">
        {imgs.map((src, i) => (
          <div
            key={i}
            className={`wr-hero__imglayer ${i === activeIdx ? "is-active" : ""}`}
            style={{ backgroundImage: `url("${src}")` }}
            aria-hidden={i === activeIdx ? "false" : "true"}
          />
        ))}
        <div className="wr-hero__veil" />
      </div>

      {/* centered hero copy */}
      <div className="wr-hero__copy">
        <h1 className="wr-hero__title">World Rated Hotels</h1>
        <p className="wr-hero__tagline">
          A private index of stays I actually slept in — design, soul, light,
          and a sense of place.
        </p>
        <p className="wr-hero__inquiry">
          For bespoke bookings and travel design,&nbsp;
          <a
            href="mailto:contact@lorranlima.com?subject=Private%20Itinerary%20%26%20Bespoke%20Bookings&body=Hello%20Lorran%20Lima%20Studio%2C%0A%0AI%E2%80%99m%20interested%20in%20bespoke%20travel%20planning%20and%20bookings.%0A%0ADestination(s):%20%0ADates%20(flexible%3F):%20%0ATravelers:%20%0AStyle%20%26%20priorities%20(e.g.%20design%20hotels%2C%20remote%20escapes%2C%20culture%2C%20dining):%20%0ABudget%20range:%20%0ANotes%20%2F%20must-sees:%20%0A%0AThank%20you%2C%0A[Your%20Name]"
            className="wr-link"
          >
            contact us here
          </a>
        </p>
      </div>

      {/* scroll cue */}
      <button
        type="button"
        className="wr-scrollcue"
        aria-label="Scroll to hotels"
        onClick={onScrollCueClick}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 16l-6-6h12l-6 6z" fill="currentColor" />
        </svg>
      </button>
      <div className="wr-scrollcue__text">Explore</div>
    </section>
  );
}

/* ---------- Main ---------- */
export default function WorldRated(props) {
  const {
    continents = [],
    countries = [],
    cities = [],
    hotels = [],
    hotelsAtoZ = [],
    selection = {},
    loading = false,
    error = "",
    viewMode = "hierarchy",

    fetchAllHotels,
    onSelectContinent,
    onSelectCountry,
    onSelectCity,
    onSetViewMode,

    // background slideshow for hero
    heroImages = [
      "https://lvltravel.s3.us-east-2.amazonaws.com/hotels/hotelMobile1.jpg",
      "https://lvltravel.s3.us-east-2.amazonaws.com/hotels/hotelImages1.jpg",
      "https://lvltravel.s3.us-east-2.amazonaws.com/hotels/hotelImages2.jpg",
      "https://lvltravel.s3.us-east-2.amazonaws.com/hotels/hotelImages3.jpg",
      "https://lvltravel.s3.us-east-2.amazonaws.com/hotels/hotelImages4.jpg",
      "https://lvltravel.s3.us-east-2.amazonaws.com/hotels/hotelImages5.jpg",
    ],
  } = props || {};

  // which hotel is open in overlay
  const [activeHotel, setActiveHotel] = useState(null);

  // ref so we can smooth scroll from the hero scroll cue
  const filtersRef = useRef(null);

  // fetch master list + default to alphabetical on mount
  useEffect(() => {
    fetchAllHotels?.();
    onSetViewMode?.("alphabetical");
  }, [fetchAllHotels, onSetViewMode]);

  // scroll reveal for cards
  useEffect(() => {
    const els = document.querySelectorAll(".wr-reveal");
    if (!els.length || typeof window.IntersectionObserver === "undefined") return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "8% 0% -6% 0%" }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [hotels, hotelsAtoZ, viewMode]);

  // header line ("All Hotels — A–Z", etc)
  const headerLine = (() => {
    if (viewMode === "alphabetical") return "All Hotels — A–Z";
    if (selection.city)
      return `Hotels in ${selection.city}${
        selection.country ? `, ${selection.country}` : ""
      }`;
    if (selection.country) return `Cities in ${selection.country}`;
    if (selection.continent) return `Countries in ${selection.continent}`;
    return "Browse by Region";
  })();

  // which list to show in the grid
  const currentHotelList =
    viewMode === "alphabetical"
      ? hotelsAtoZ
      : selection.city
      ? hotels
      : [];

  const showGrid =
    (viewMode === "alphabetical" && (hotelsAtoZ || []).length > 0) ||
    (viewMode === "hierarchy" && selection.city && (hotels || []).length > 0);

  // visibility of each filter row
  const continentRowVisible = viewMode === "hierarchy";
  const countryRowVisible =
    viewMode === "hierarchy" && !!selection.continent && countries.length > 0;
  const cityRowVisible =
    viewMode === "hierarchy" && !!selection.country && cities.length > 0;

  // smooth scroll down from hero "Explore"
  const handleScrollCue = () => {
    if (!filtersRef.current) return;
    filtersRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="worldrated">
      {/* ===== HERO ===== */}
      <Hero imgs={heroImages} onScrollCueClick={handleScrollCue} />

      {/* ===== CONTENT BELOW HERO ===== */}
      <div className="worldrated__glasswrap">
        {/* top header / view toggle / header line */}
        <header className="worldrated__top">
          <div className="worldrated__top-main">
            <p className="worldrated__subtitle">Curated stays across continents</p>
          </div>

          <div className="worldrated__top-side">
            <ViewToggle viewMode={viewMode} onSetViewMode={onSetViewMode} />
            <div className="worldrated__headerline">{headerLine}</div>
          </div>
        </header>

        <div className="worldrated__divider" />

        {/* ===== FILTERS ===== */}
        <section className="wr-filtershell" ref={filtersRef}>
          {continentRowVisible && (
            <div className="wr-filterrow">
              <div className="wr-rowlabel">Continent</div>
              <div className="wr-rowchips-outer">
                <div className="wr-rowchips">
                  {(continents.length
                    ? continents
                    : [
                        "Africa",
                        "Asia",
                        "Europe",
                        "North America",
                        "South America",
                        "Australia",
                        "Antarctica",
                      ]
                  ).map((c) => (
                    <Pill
                      key={c}
                      active={selection.continent === c}
                      onClick={() => onSelectContinent?.(c)}
                    >
                      {c}
                    </Pill>
                  ))}
                </div>
              </div>
            </div>
          )}

          {countryRowVisible && (
            <div className="wr-filterrow">
              <div className="wr-rowlabel">Country</div>
              <div className="wr-rowchips-outer">
                <div className="wr-rowchips">
                  {(countries || []).map((c) => (
                    <Pill
                      key={c}
                      active={selection.country === c}
                      onClick={() => onSelectCountry?.(c, selection.continent)}
                    >
                      {c}
                    </Pill>
                  ))}
                </div>
              </div>
            </div>
          )}

          {cityRowVisible && (
            <div className="wr-filterrow">
              <div className="wr-rowlabel">City</div>
              <div className="wr-rowchips-outer">
                <div className="wr-rowchips">
                  {(cities || []).map((c) => (
                    <Pill
                      key={c}
                      active={selection.city === c}
                      onClick={() =>
                        onSelectCity?.(c, selection.continent, selection.country)
                      }
                    >
                      {c}
                    </Pill>
                  ))}
                </div>
              </div>
            </div>
          )}

          {viewMode === "alphabetical" && (
            <div className="wr-filterrow">
              <div className="wr-rowlabel"></div>
              <div className="wr-rowchips-outer">
                <div className="wr-rowchips">
                  <span className="wr-rowchips-note">
                    Showing full index of rated stays.
                  </span>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Loading / error */}
        {loading && <div className="wr-status">Loading hotels…</div>}
        {!!error && (
          <div className="wr-status" style={{ color: "crimson" }}>
            Error: {String(error)}
          </div>
        )}

        {/* ===== RESULTS GRID ===== */}
        <section className="wr-results">
          {showGrid ? (
            <div className="wr-grid wr-fadein">
              {currentHotelList.map((h) => (
                <HotelCard
                  key={h.id || `${h.name}-${h.city}-${h.country}`}
                  h={h}
                  onSelect={setActiveHotel}
                />
              ))}
            </div>
          ) : (
            !loading &&
            !error && (
              <div className="wr-status wr-status-empty">
                {viewMode === "alphabetical"
                  ? "No hotels to show (yet)."
                  : selection.city
                  ? "No hotels in this city (yet)."
                  : selection.country
                  ? "Select a city."
                  : selection.continent
                  ? "Select a country."
                  : "Select a continent to begin."}
              </div>
            )
          )}
        </section>
      </div>

      {/* ===== FULLSCREEN DETAIL OVERLAY ===== */}
      {activeHotel && (
        <WorldRatedDetail
          hotel={activeHotel}
          onClose={() => setActiveHotel(null)}
        />
      )}
    </div>
  );
}
